(function () {
  'use strict';

  const track = (eventName, data = {}) => {
    const payload = { event: eventName, page_type: 'product', ...data };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent('wig:analytics', { detail: payload }));
  };

  const getProductJson = (section) => {
    const el = section.querySelector('[id^="wig-product-json-"]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const formatMoney = (cents, product) => {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  };

  const isVariantAvailable = (variant) => {
    if (!variant) return false;
    const value = variant.available;
    if (value === false || value === 'false' || value === 0 || value === '0') return false;
    return true;
  };

  const escapeValue = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  };

  const pickFirstAvailableVariant = (product) =>
    product.variants.find((variant) => isVariantAvailable(variant)) || product.variants[0] || null;

  const setActiveOptionChip = (input) => {
    const list = input.closest('[data-option-values]');
    list?.querySelectorAll('.wig-pdp__pill, .wig-pdp-buy__chip').forEach((el) => el.classList.remove('is-active'));
    input.closest('.wig-pdp__pill, .wig-pdp-buy__chip')?.classList.add('is-active');
  };

  const selectVariantOptions = (form, variant) => {
    if (!variant?.options?.length) return;
    variant.options.forEach((value, index) => {
      const input = form.querySelector(`input[data-option-index="${index}"][value="${escapeValue(value)}"]`);
      if (input) {
        input.checked = true;
        setActiveOptionChip(input);
      }
    });
  };

  const openCartDrawer = (detail = {}) => {
    if (detail.open === false) return false;

    document.dispatchEvent(
      new CustomEvent('wevolt:cart:open', {
        bubbles: true,
        detail,
      })
    );

    return Boolean(document.querySelector('[data-wevolt-cart-drawer]') || window.wevoltCartDrawer);
  };

  const addProductToCart = async (form, variant, product) => {
    if (!isVariantAvailable(variant)) return false;

    const atcButton = form.querySelector('[data-atc-button]');
    const originalLabel = atcButton?.textContent;
    if (atcButton) {
      atcButton.disabled = true;
      atcButton.textContent = 'Adding...';
    }

    const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
    const sectionIds = getCartSectionIds();
    const basePayload = {
      items: [{ id: Number(variant.id), quantity }],
    };

    const postAdd = async (payload) => {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');
      return data;
    };

    try {
      let data;
      try {
        data = await postAdd(sectionIds.length ? { ...basePayload, sections: sectionIds.join(',') } : basePayload);
      } catch (firstError) {
        if (sectionIds.length) {
          data = await postAdd(basePayload);
        } else {
          throw firstError;
        }
      }

      await updateCartSections(data.sections);
      if (window.WigCart?.resolveCartCount) {
        data.item_count = await window.WigCart.resolveCartCount(data);
      } else if (data.item_count == null && data.items) {
        data.item_count = data.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      }
      dispatchCartUpdate(data);
      track('add_to_cart', {
        variant_id: variant.id,
        variant_title: variant.title,
        quantity,
        value: variant.price,
      });
      openCartDrawer({ sections: data.sections, itemCount: data.item_count, open: true });
      return true;
    } catch (error) {
      console.error('[wig-pdp] add to cart failed', error);
      if (statusElForForm(form)) {
        const status = statusElForForm(form);
        status.textContent = error.message || 'Could not add to cart. Please try again.';
        status.classList.remove('hidden');
      }
      return false;
    } finally {
      if (atcButton) {
        atcButton.disabled = !isVariantAvailable(variant);
        atcButton.textContent = isVariantAvailable(variant) ? (originalLabel || 'Add To Cart') : 'Sold Out';
      }
    }
  };

  const statusElForForm = (form) => form.closest('.wig-pdp')?.querySelector('[data-variant-status]');

  const dispatchCartUpdate = (data, source = 'wig-pdp') => {
    const itemCount = data?.item_count ?? data?.items?.reduce((sum, item) => sum + item.quantity, 0) ?? null;

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          data: {
            itemCount,
            sections: data?.sections,
            source,
          },
        },
      })
    );

    document.dispatchEvent(
      new CustomEvent('wevolt:cart:updated', {
        detail: { itemCount, sections: data?.sections },
      })
    );

    if (itemCount != null) {
      document.querySelectorAll('.wig-header__cart-count').forEach((el) => {
        el.textContent = String(itemCount);
        el.classList.toggle('is-empty', itemCount === 0);
      });
    }
  };

  const getCartSectionIds = () => {
    const wevoltId = document.querySelector('[data-wevolt-cart-drawer]')?.dataset.sectionId;
    return wevoltId ? [wevoltId] : [];
  };

  const updateCartSections = async (sectionsHtml) => {
    if (!sectionsHtml) return;

    const wevoltId = document.querySelector('[data-wevolt-cart-drawer]')?.dataset.sectionId;
    if (wevoltId && sectionsHtml[wevoltId] && window.wevoltCartDrawer?.applySections) {
      await window.wevoltCartDrawer.applySections(sectionsHtml, wevoltId);
      return;
    }

    Object.entries(sectionsHtml).forEach(([id, html]) => {
      if (id === 'cart-drawer-section') return;
      const target = document.getElementById(`shopify-section-${id}`) || document.getElementById(id);
      if (target && html) target.innerHTML = html;
    });
  };

  const initVariantPicker = (section, product) => {
    const form = section.querySelector('[data-product-form]');
    if (!form) return;

    const variantInput = form.querySelector('[data-variant-id]');
    const optionGroups = product.has_only_default_variant ? [] : [...section.querySelectorAll('[data-option-values]')];
    const statusEl = section.querySelector('[data-variant-status]');

    const getSelectedOptions = () =>
      product.options.map((_, index) => {
        const input = form.querySelector(`input[data-option-index="${index}"]:checked`);
        return input ? input.value : null;
      });

    const findVariant = (options) =>
      product.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );

    const resolveVariant = ({ scrollOnMissing = false } = {}) => {
      if (product.has_only_default_variant) {
        return product.variants[0] || null;
      }

      const selected = getSelectedOptions();
      const missingIndex = selected.findIndex((value) => !value);

      optionGroups.forEach((group, index) => {
        const error = group.closest('[data-option-index]')?.querySelector('[data-option-error]');
        const hasValue = Boolean(selected[index]);
        group.closest('.wig-pdp__option, .wig-pdp-buy__option')?.classList.toggle('has-error', scrollOnMissing && !hasValue);
        if (error) error.classList.toggle('hidden', hasValue || !scrollOnMissing);
      });

      if (missingIndex >= 0) {
        if (scrollOnMissing) {
          section.querySelector(`[data-option-index="${missingIndex}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        const hiddenId = Number(variantInput?.value);
        const fallback = hiddenId ? product.variants.find((v) => v.id === hiddenId) : null;
        return fallback || null;
      }

      return findVariant(selected) || null;
    };

    const updateAvailability = () => {
      if (product.has_only_default_variant) return;
      const selected = getSelectedOptions();

      optionGroups.forEach((group, optionIndex) => {
        group.querySelectorAll('[data-option-value]').forEach((label) => {
          const value = label.dataset.optionValue;
          const testOptions = [...selected];
          testOptions[optionIndex] = value;
          const available = product.variants.some(
            (variant) =>
              isVariantAvailable(variant) &&
              variant.options.every((opt, i) => !testOptions[i] || opt === testOptions[i])
          );
          label.classList.toggle('is-disabled', !available);
          const input = label.querySelector('input');
          if (input) input.disabled = !available;
        });
      });
    };

    const syncVariant = ({ scrollOnMissing = false } = {}) => {
      let variant = resolveVariant({ scrollOnMissing });

      if (variant && !isVariantAvailable(variant) && !scrollOnMissing) {
        const fallback = pickFirstAvailableVariant(product);
        if (fallback && fallback.id !== variant.id) {
          selectVariantOptions(form, fallback);
          variant = fallback;
        }
      }

      if (!variant) return null;

      if (variantInput) variantInput.value = variant.id;

      if (!product.has_only_default_variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.querySelectorAll('[data-current-price]').forEach((el) => {
        el.textContent = formatMoney(variant.price, product);
      });

      const discountAmountCents = Number(form.dataset.discountAmountCents || 0);
      const discountPercent = Number(form.dataset.discountPercent || 0);
      document.querySelectorAll('[data-code-price]').forEach((el) => {
        if (discountAmountCents > 0) {
          el.textContent = formatMoney(Math.max(0, variant.price - discountAmountCents), product);
        } else if (discountPercent > 0) {
          el.textContent = formatMoney(Math.round(variant.price * (100 - discountPercent) / 100), product);
        }
      });

      document.querySelectorAll('[data-promo-off-label]').forEach((el) => {
        if (discountAmountCents > 0) {
          el.textContent = `Save ${formatMoney(discountAmountCents, product)} today — use CODE:`;
        }
      });

      document.querySelectorAll('[data-compare-price]').forEach((el) => {
        if (variant.compare_at_price > variant.price) {
          el.textContent = formatMoney(variant.compare_at_price, product);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      document.querySelectorAll('[data-save-badge]').forEach((el) => {
        if (variant.compare_at_price > variant.price) {
          el.textContent = `Save ${formatMoney(variant.compare_at_price - variant.price, product)}`;
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      document.querySelectorAll('[data-sticky-price]').forEach((el) => {
        el.textContent = formatMoney(variant.price, product);
      });

      document.querySelectorAll('[data-sticky-variant]').forEach((el) => {
        el.textContent = variant.title;
      });

      document.querySelectorAll('[data-installment-variant-id]').forEach((el) => {
        el.value = variant.id;
      });

      const atcButton = section.querySelector('[data-atc-button]');
      const buyNowButton = section.querySelector('[data-buy-now]');
      if (atcButton) {
        atcButton.disabled = !isVariantAvailable(variant);
        atcButton.textContent = isVariantAvailable(variant) ? 'Add To Cart' : 'Sold Out';
      }
      if (buyNowButton) buyNowButton.disabled = !isVariantAvailable(variant);

      document.querySelectorAll('[data-sticky-atc]').forEach((el) => {
        el.disabled = !isVariantAvailable(variant);
        el.textContent = isVariantAvailable(variant) ? 'Add To Cart' : 'Sold Out';
      });

      if (statusEl) {
        statusEl.textContent = isVariantAvailable(variant) ? '' : 'This combination is currently unavailable.';
        statusEl.classList.toggle('hidden', isVariantAvailable(variant));
      }

      if (variant.featured_media) {
        section.querySelector(`[data-media-id="${variant.featured_media.id}"]`)?.click();
      }

      if (!scrollOnMissing) {
        track('variant_select', {
          variant_id: variant.id,
          variant_title: variant.title,
          price: variant.price,
          available: variant.available,
        });
      }

      product.options.forEach((name, index) => {
        const label = section.querySelector(`[data-selected-label="${index}"]`);
        if (label) label.textContent = getSelectedOptions()[index] || '';
      });

      updateAvailability();
      return variant;
    };

    const handleAddToCart = async (event) => {
      event?.preventDefault();
      const variant = syncVariant({ scrollOnMissing: true });
      if (!isVariantAvailable(variant)) return;
      await addProductToCart(form, variant, product);
    };

    if (product.has_only_default_variant) {
      form.addEventListener('submit', handleAddToCart);
      form.querySelector('[data-buy-now]')?.addEventListener('click', async (event) => {
        event.preventDefault();
        const variant = product.variants[0];
        if (!isVariantAvailable(variant)) return;
        const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: qty }] }),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || 'Could not add to cart');
          }
          track('checkout_start', { source: 'buy_now' });
          window.location.href = '/checkout';
        } catch (error) {
          console.error(error);
        }
      });
      syncVariant();
      track('landing_view', { product_id: product.id, product_handle: product.handle, page_version: 'wig-pdp-v2' });
      return;
    }

    const bootstrapVariantSelection = () => {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('variant') || variantInput?.value;
      let variant = variantId ? product.variants.find((v) => String(v.id) === String(variantId)) : null;

      product.options.forEach((name, index) => {
        const param = params.get(name.toLowerCase().replace(/\s+/g, '_'));
        if (!param) return;
        const input = form.querySelector(`input[data-option-index="${index}"][value="${escapeValue(param)}"]`);
        if (input) {
          input.checked = true;
          setActiveOptionChip(input);
        }
      });

      const selected = getSelectedOptions();
      if (!selected.includes(null)) {
        variant = findVariant(selected) || variant;
      }

      if (!variant || !isVariantAvailable(variant)) {
        variant = pickFirstAvailableVariant(product);
      }

      if (variant) {
        selectVariantOptions(form, variant);
        if (variantInput) variantInput.value = variant.id;
      }

      syncVariant();
    };

    form.querySelectorAll('input[type="radio"][data-option-index]').forEach((input) => {
      input.addEventListener('change', () => {
        const list = input.closest('[data-option-values]');
        list?.querySelectorAll('.wig-pdp__pill, .wig-pdp-buy__chip').forEach((el) => el.classList.remove('is-active'));
        input.closest('.wig-pdp__pill, .wig-pdp-buy__chip')?.classList.add('is-active');
        syncVariant();
      });
    });

    form.addEventListener('submit', handleAddToCart);

    form.querySelector('[data-buy-now]')?.addEventListener('click', async (event) => {
      event.preventDefault();
      const variant = syncVariant({ scrollOnMissing: true });
      if (!isVariantAvailable(variant)) return;
      const qty = Number(form.querySelector('[name="quantity"]')?.value || 1);
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: qty }] }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Could not add to cart');
        }
        track('checkout_start', { source: 'buy_now' });
        window.location.href = '/checkout';
      } catch (error) {
        console.error(error);
      }
    });

    updateAvailability();
    bootstrapVariantSelection();
    track('landing_view', {
      product_id: product.id,
      product_handle: product.handle,
      page_version: 'wig-pdp-v2',
    });
  };

  const initHeroVideo = (section) => {
    const video = section.querySelector('.wig-pdp__hero-video');
    if (!video) return;

    track('hero_video_start', { video_id: video.dataset.mediaId || 'hero' });

    video.addEventListener('play', () => track('hero_video_start', { replay: true }), { once: true });
    video.addEventListener('timeupdate', () => {
      if (!video.dataset.halfTracked && video.currentTime / video.duration >= 0.5) {
        video.dataset.halfTracked = 'true';
        track('hero_video_50', { video_id: video.dataset.mediaId || 'hero' });
      }
    });
    video.addEventListener('ended', () => track('hero_video_complete', { video_id: video.dataset.mediaId || 'hero' }));
    video.addEventListener('error', () => {
      const wrap = section.querySelector('[data-hero-video-wrap]');
      if (wrap) wrap.classList.add('has-error');
    });
  };

  const initStickyBar = (section) => {
    const stickyBar = document.getElementById('wig-pdp-sticky');
    const form = section.querySelector('[data-product-form]');
    if (!stickyBar || !form) return;

    stickyBar.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
      form.requestSubmit();
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.hidden = entry.isIntersecting;
        stickyBar.dataset.visible = entry.isIntersecting ? 'false' : 'true';
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(form);
  };

  const initGuideLinks = (section) => {
    section.querySelectorAll('[data-size-guide-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        track('size_guide_open');
        document.getElementById('wig-size-guide')?.scrollIntoView({ behavior: 'smooth' });
      });
    });
    section.querySelectorAll('[data-cap-guide-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        track('size_guide_open', { guide: 'cap' });
        document.getElementById('wig-size-guide')?.scrollIntoView({ behavior: 'smooth' });
      });
    });
  };

  const initExpandTracking = () => {
    document.querySelectorAll('[data-track-expand]').forEach((el) => {
      el.addEventListener('toggle', () => {
        if (el.open) {
          track(el.dataset.trackExpand || 'section_expand', { section: el.dataset.trackSection || '' });
        }
      });
    });
  };

  const initUgc = () => {
    document.querySelectorAll('.wig-pdp-ugc:not(.wig-home-ugc):not(.wig-product-ugc)').forEach((section) => {
      const cards = [...section.querySelectorAll('[data-ugc-card]')];
      const filters = [...section.querySelectorAll('[data-ugc-filter]')];

      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          const tag = filter.dataset.ugcFilter;
          filters.forEach((f) => f.classList.toggle('is-active', f === filter));
          cards.forEach((card) => {
            const tags = (card.dataset.ugcTags || '').split(',');
            card.hidden = tag !== 'all' && !tags.includes(tag);
          });
          track('ugc_filter', { filter: tag });
        });
      });
    });

    document.querySelectorAll('.wig-product-ugc, .wig-home-ugc, .wig-pdp-ugc').forEach((section) => {
      section.querySelectorAll('[data-ugc-video]').forEach((video) => {
        video.addEventListener('play', () => track('ugc_play', { id: video.dataset.ugcVideo }));
        video.addEventListener('ended', () => track('ugc_complete', { id: video.dataset.ugcVideo }));
      });
    });
  };

  const initBuyBoxExtras = (section) => {
    section.querySelectorAll('[data-copy-code]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.dataset.copyCode;
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          btn.textContent = 'Copied';
          window.setTimeout(() => { btn.textContent = 'Copy'; }, 2000);
        } catch {
          btn.textContent = code;
        }
      });
    });

    const countdown = section.querySelector('[data-countdown]');
    if (!countdown) return;

    const mode = countdown.dataset.countdownMode || 'daily';
    const fixedEnd = countdown.dataset.countdownEnd;
    const hEl = countdown.querySelector('[data-countdown-h]');
    const mEl = countdown.querySelector('[data-countdown-m]');
    const sEl = countdown.querySelector('[data-countdown-s]');

    const getEndTime = () => {
      if (mode === 'fixed' && fixedEnd) {
        const parsed = new Date(fixedEnd).getTime();
        if (!Number.isNaN(parsed)) return parsed;
      }
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
      return endOfDay.getTime();
    };

    const tick = () => {
      const diff = getEndTime() - Date.now();
      if (diff <= 0) {
        if (hEl) hEl.textContent = '00';
        if (mEl) mEl.textContent = '00';
        if (sEl) sEl.textContent = '00';
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (hEl) hEl.textContent = String(h).padStart(2, '0');
      if (mEl) mEl.textContent = String(m).padStart(2, '0');
      if (sEl) sEl.textContent = String(s).padStart(2, '0');
    };

    tick();
    window.setInterval(tick, 1000);
  };

  const initProductGallery = (section, product) => {
    const useVideo916 = section.dataset.videoPortrait916 !== 'false';

    const gallery = section.querySelector('.wig-pdp__gallery');
    if (!gallery || !product.media?.length) return;

    const hero = gallery.querySelector('[data-hero-media]');
    const track = gallery.querySelector('[data-pdp-thumbs-track]');
    const thumbs = track ? [...track.querySelectorAll('.wig-spotlight__thumb')] : [...gallery.querySelectorAll('.wig-spotlight__thumb')];
    if (!hero || thumbs.length < 1) return;

    const carousel = gallery.querySelector('[data-pdp-thumbs-carousel]');
    const prevBtn = carousel?.querySelector('[data-pdp-thumbs-prev]');
    const nextBtn = carousel?.querySelector('[data-pdp-thumbs-next]');

    const sectionId = section.dataset.sectionId || 'main';
    const mainId = `wig-spotlight-main-${sectionId}`;

    const scrollThumbIntoView = (thumb) => {
      if (!track || !thumb) return;
      const thumbLeft = thumb.offsetLeft;
      const thumbRight = thumbLeft + thumb.offsetWidth;
      const viewLeft = track.scrollLeft;
      const viewRight = viewLeft + track.clientWidth;

      if (thumbLeft < viewLeft) {
        track.scrollTo({ left: thumbLeft, behavior: 'smooth' });
      } else if (thumbRight > viewRight) {
        track.scrollTo({ left: thumbRight - track.clientWidth, behavior: 'smooth' });
      }
    };

    const updateNavButtons = () => {
      if (!track || !prevBtn || !nextBtn) return;
      const overflow = track.scrollWidth > track.clientWidth + 4;
      prevBtn.classList.toggle('is-hidden', !overflow);
      nextBtn.classList.toggle('is-hidden', !overflow);
      if (!overflow) return;
      prevBtn.disabled = track.scrollLeft <= 2;
      nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    };

    const scrollThumbs = (dir) => {
      if (!track) return;
      const thumb = track.querySelector('.wig-spotlight__thumb');
      const gap = 10;
      const amount = thumb ? thumb.offsetWidth + gap : track.clientWidth * 0.8;
      track.scrollBy({ left: dir * amount, behavior: 'smooth' });
    };

    let thumbDragMoved = false;

    if (track) {
      prevBtn?.addEventListener('click', () => scrollThumbs(-1));
      nextBtn?.addEventListener('click', () => scrollThumbs(1));
      track.addEventListener('scroll', updateNavButtons, { passive: true });
      window.addEventListener('resize', updateNavButtons);
      updateNavButtons();

      const drag = { active: false, moved: false, startX: 0, scrollLeft: 0, pointerId: null };

      track.addEventListener('pointerdown', (event) => {
        drag.active = true;
        drag.moved = false;
        thumbDragMoved = false;
        drag.startX = event.clientX;
        drag.scrollLeft = track.scrollLeft;
        drag.pointerId = event.pointerId;
      });

      track.addEventListener('pointermove', (event) => {
        if (!drag.active || event.pointerId !== drag.pointerId) return;
        const deltaX = event.clientX - drag.startX;
        if (!drag.moved && Math.abs(deltaX) > 6) {
          drag.moved = true;
          thumbDragMoved = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }
        if (drag.moved) {
          event.preventDefault();
          track.scrollLeft = drag.scrollLeft - deltaX;
        }
      });

      const endDrag = (event) => {
        if (!drag.active || event.pointerId !== drag.pointerId) return;
        drag.active = false;
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        updateNavButtons();
        if (thumbDragMoved) {
          window.setTimeout(() => {
            thumbDragMoved = false;
          }, 0);
        }
      };

      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
    }

    const setActiveThumb = (index) => {
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
      scrollThumbIntoView(thumbs[index]);
      window.setTimeout(updateNavButtons, 320);
    };

    const renderImage = (media, thumb) => {
      const src = thumb?.dataset.src || media.preview_image?.src || media.src;
      hero.innerHTML = '';
      const img = document.createElement('img');
      img.id = mainId;
      img.src = src;
      img.alt = media.alt || product.title;
      img.loading = 'eager';
      img.dataset.heroImage = '';
      hero.appendChild(img);
    };

    const renderVideo = (media) => {
      const sources = (media.sources || []).filter((s) => s.mime_type?.includes('video'));
      const poster = media.preview_image?.src || '';
      hero.innerHTML = '';
      const wrap = document.createElement('div');
      wrap.className = 'wig-pdp__video-wrap';
      wrap.dataset.heroVideoWrap = '';
      if (useVideo916) {
        wrap.classList.add('wig-pdp__video-wrap--916');
        wrap.dataset.videoAspect = '9:16';
      }
      const video = document.createElement('video');
      video.className = useVideo916 ? 'wig-pdp__hero-video wig-pdp__hero-video--916' : 'wig-pdp__hero-video';
      video.controls = true;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      if (poster) video.poster = poster;
      if (sources.length) {
        sources.forEach((source) => {
          const el = document.createElement('source');
          el.src = source.url;
          el.type = source.mime_type;
          video.appendChild(el);
        });
      } else if (media.sources?.[0]?.url) {
        video.src = media.sources[0].url;
      }
      wrap.appendChild(video);
      hero.appendChild(wrap);
      video.play().catch(() => {});
    };

    const showMedia = (index) => {
      const thumb = thumbs[index];
      if (!thumb) return;
      const mediaId = Number(thumb.dataset.mediaId);
      const media = product.media.find((item) => item.id === mediaId);
      if (!media) return;

      setActiveThumb(index);
      currentIndex = index;
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      if (media.media_type === 'video') {
        renderVideo(media);
      } else {
        renderImage(media, thumb);
      }
    };

    let currentIndex = 0;
    const dots = [];
    const mainShell = gallery.querySelector('.wig-spotlight__main-shell');

    if (mainShell && thumbs.length > 1) {
      const dotsWrap = document.createElement('div');
      dotsWrap.className = 'wig-pdp__gallery-dots';
      dotsWrap.setAttribute('aria-hidden', 'true');

      thumbs.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'wig-pdp__gallery-dot' + (index === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Show image ${index + 1}`);
        dot.addEventListener('click', () => showMedia(index));
        dotsWrap.appendChild(dot);
        dots.push(dot);
      });

      mainShell.appendChild(dotsWrap);
    }

    if (thumbs.length > 1) {
      const swipeTarget = mainShell || hero;
      const mobileGalleryMq = window.matchMedia('(max-width: 749px)');

      const initMobileHeroSwipe = (target) => {
        if (!target) return;

        let startX = 0;
        let startY = 0;
        let tracking = false;

        const handleSwipe = (endX, endY) => {
          if (!mobileGalleryMq.matches) return;
          const dx = endX - startX;
          const dy = endY - startY;
          if (Math.abs(dx) < 36) return;
          if (Math.abs(dx) < Math.abs(dy) * 1.15) return;

          if (dx < 0 && currentIndex < thumbs.length - 1) {
            showMedia(currentIndex + 1);
          } else if (dx > 0 && currentIndex > 0) {
            showMedia(currentIndex - 1);
          }
        };

        target.addEventListener(
          'touchstart',
          (event) => {
            if (!mobileGalleryMq.matches || !event.touches[0]) return;
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
            tracking = true;
          },
          { passive: true }
        );

        target.addEventListener(
          'touchend',
          (event) => {
            if (!tracking || !mobileGalleryMq.matches) return;
            tracking = false;
            const touch = event.changedTouches[0];
            if (!touch) return;
            handleSwipe(touch.clientX, touch.clientY);
          },
          { passive: true }
        );

        target.addEventListener(
          'touchcancel',
          () => {
            tracking = false;
          },
          { passive: true }
        );
      };

      initMobileHeroSwipe(swipeTarget);
    }

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', (event) => {
        if (thumbDragMoved) {
          event.preventDefault();
          return;
        }
        event.preventDefault();
        showMedia(index);
      });
    });
  };

  document.querySelectorAll('.wig-pdp').forEach((section) => {
    const product = getProductJson(section);
    if (!product) return;
    initVariantPicker(section, product);
    initProductGallery(section, product);
    initHeroVideo(section);
    initStickyBar(section);
    initGuideLinks(section);
    initBuyBoxExtras(section);
  });

  initExpandTracking();
  initUgc();

  document.addEventListener('click', (event) => {
    const checkoutBtn = event.target.closest('#checkout, [name="checkout"]');
    if (checkoutBtn) track('checkout_start');
  });
})();
